import type { PagedResultDto } from '@abp/ng.core';
import { RestService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import {
  GetIntegrationsInput,
  ChannelIntegrationCreateUpdateDto,
  ChannelIntegrationDto,
  ServiceIntegrationDto,
  RavenMetaDataListDto,
  IntegrationAvailabilityDto,
  GetLlmConfigurationsInput,
  LlmConfigurationSummaryDto,
  LlmConfigurationDto,
  LlmConfigurationProviderModelsDto,
  IntegrationSupportedLanguagesDto,
  GetMcpServersInput,
  McpServerSummaryDto,
  McpServerDto,
} from './integration.model';
import { UserLlmConfigurationDto, UserGuardrailDto } from 'src/app/ai-agents/proxy/ai-agents.model';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IntegrationService {
  apiName = 'Default';
  userLlmSettingsPatchedSubject = new Subject<void>();

  createChannelIntegration = (input: ChannelIntegrationCreateUpdateDto) =>
    this.restService.request<ChannelIntegrationCreateUpdateDto, ChannelIntegrationDto>(
      {
        method: 'POST',
        url: '/api/app/channel-integrations',
        body: input,
      },
      { apiName: this.apiName },
    );

  createServiceIntegration = (input: ChannelIntegrationCreateUpdateDto) =>
    this.restService.request<ChannelIntegrationCreateUpdateDto, ServiceIntegrationDto>(
      {
        method: 'POST',
        url: '/api/app/service-integrations',
        body: input,
      },
      { apiName: this.apiName },
    );

  deleteChannelIntegration = (id: string) =>
    this.restService.request<void, void>(
      {
        method: 'DELETE',
        url: `/api/app/channel-integrations/${id}`,
      },
      { apiName: this.apiName },
    );

  deleteServiceIntegration = (id: string) =>
    this.restService.request<void, void>(
      {
        method: 'DELETE',
        url: `/api/app/service-integrations/${id}`,
      },
      { apiName: this.apiName },
    );

  getChannelIntegration = (id: string) =>
    this.restService.request<void, ChannelIntegrationDto>(
      {
        method: 'GET',
        url: `/api/app/channel-integrations/${id}`,
      },
      { apiName: this.apiName },
    );

  getServiceIntegration = (id: string) =>
    this.restService.request<void, ServiceIntegrationDto>(
      {
        method: 'GET',
        url: `/api/app/service-integrations/${id}`,
      },
      { apiName: this.apiName },
    );

  getHostServiceIntegration = (id: string) =>
    this.restService.request<void, ServiceIntegrationDto>(
      {
        method: 'GET',
        url: `/api/app/service-integrations/host/${id}`,
      },
      { apiName: this.apiName },
    );

  getChannelIntegrationList = (input: GetIntegrationsInput) =>
    this.restService.request<void, PagedResultDto<ChannelIntegrationDto>>(
      {
        method: 'GET',
        url: '/api/app/channel-integrations',
        params: {
          filterText: input.filterText,
          name: input.name,
          sorting: input.sorting,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
          type: input.type,
        },
      },
      { apiName: this.apiName },
    );

  getServiceIntegrationList = (input: GetIntegrationsInput) =>
    this.restService.request<void, PagedResultDto<ServiceIntegrationDto>>(
      {
        method: 'GET',
        url: '/api/app/service-integrations',
        params: {
          filterText: input.filterText,
          name: input.name,
          sorting: input.sorting,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
          type: input.type,
        },
      },
      { apiName: this.apiName },
    );

  updateChannelIntegration = (id: string, input: ChannelIntegrationCreateUpdateDto) =>
    this.restService.request<ChannelIntegrationCreateUpdateDto, ChannelIntegrationDto>(
      {
        method: 'PUT',
        url: `/api/app/channel-integrations/${id}`,
        body: input,
      },
      { apiName: this.apiName },
    );

  updateServiceIntegration = (id: string, input: ChannelIntegrationCreateUpdateDto) =>
    this.restService.request<ChannelIntegrationCreateUpdateDto, ServiceIntegrationDto>(
      {
        method: 'PUT',
        url: `/api/app/service-integrations/${id}`,
        body: input,
      },
      { apiName: this.apiName },
    );

  getChannelIntegrationTypes = () =>
    this.restService.request<void, RavenMetaDataListDto>(
      {
        method: 'GET',
        url: '/api/app/channel-integrations/channel-integration-name-list',
      },
      { apiName: this.apiName },
    );

  getServiceIntegrationTypes = () =>
    this.restService.request<void, RavenMetaDataListDto>(
      {
        method: 'GET',
        url: '/api/app/service-integrations/service-integration-name-list',
      },
      { apiName: this.apiName },
    );

  getWebChatApiUrl = () =>
    this.restService.request<void, string[]>(
      {
        method: 'GET',
        url: '/api/app/channel-integrations/getWebChatApiUrl',
      },
      { apiName: this.apiName },
    );

  getIntegrationAvailabilities = () =>
    this.restService.request<void, IntegrationAvailabilityDto[]>(
      {
        method: 'GET',
        url: '/api/app/service-integrations/integration-availabilities',
      },
      { apiName: this.apiName },
    );

  getServiceIntegrationSupportedLanguages = () =>
    this.restService.request<void, IntegrationSupportedLanguagesDto[]>(
      {
        method: 'GET',
        url: '/api/app/service-integrations/supported-languages',
      },
      { apiName: this.apiName },
    );

  getLlmConfigurationList = (input: GetLlmConfigurationsInput) =>
    this.restService.request<GetLlmConfigurationsInput, PagedResultDto<LlmConfigurationSummaryDto>>(
      {
        method: 'GET',
        url: '/api/app/llm-configurations',
        params: { ...input },
      },
      { apiName: this.apiName },
    );

  getLlmConfiguration = (id: string) =>
    this.restService.request<void, LlmConfigurationDto>(
      {
        method: 'GET',
        url: `/api/app/llm-configurations/${id}`,
      },
      { apiName: this.apiName },
    );

  deleteLlmConfiguration = (id: string) =>
    this.restService.request<void, void>(
      {
        method: 'DELETE',
        url: `/api/app/llm-configurations/${id}`,
      },
      { apiName: this.apiName },
    );

  createLlmConfiguration = (input: LlmConfigurationDto) =>
    this.restService.request<LlmConfigurationDto, LlmConfigurationDto>(
      {
        method: 'POST',
        url: '/api/app/llm-configurations',
        body: input,
      },
      { apiName: this.apiName },
    );

  updateLlmConfiguration = (id: string, input: LlmConfigurationDto) =>
    this.restService.request<LlmConfigurationDto, LlmConfigurationDto>(
      {
        method: 'PUT',
        url: `/api/app/llm-configurations/${id}`,
        body: input,
      },
      { apiName: this.apiName },
    );

  checkLlmConfigurationAvailability = () =>
    this.restService.request<void, boolean>(
      {
        method: 'GET',
        url: '/api/app/llm-configurations/check-availability',
      },
      { apiName: this.apiName },
    );

  getAvailableLlmConfigurations = () =>
    this.restService.request<void, LlmConfigurationSummaryDto[]>(
      {
        method: 'GET',
        url: '/api/app/llm-configurations/available',
      },
      { apiName: this.apiName },
    );

  getUserLlmConfiguration = () =>
    this.restService.request<void, UserLlmConfigurationDto>(
      {
        method: 'GET',
        url: `/api/app/llm-configurations/user-llm-configuration`,
      },
      { apiName: this.apiName },
    );

  saveUserLlmConfiguration = (input: UserLlmConfigurationDto) =>
    this.restService.request<UserLlmConfigurationDto, void>(
      {
        method: 'POST',
        url: `/api/app/llm-configurations/save-user-llm-configuration`,
        body: input,
      },
      { apiName: this.apiName },
    );

  getSuggestedModels = () =>
    this.restService.request<void, LlmConfigurationProviderModelsDto[]>(
      {
        method: 'GET',
        url: '/api/app/llm-configurations/suggested-models',
      },
      { apiName: this.apiName },
    );

  getUserGuardrailConfiguration = () =>
    this.restService.request<void, UserGuardrailDto>(
      {
        method: 'GET',
        url: `/api/app/guardrails/user-guardrail`,
      },
      { apiName: this.apiName },
    );

  saveUserGuardrailConfiguration = (input: UserGuardrailDto) =>
    this.restService.request<UserGuardrailDto, void>(
      {
        method: 'POST',
        url: `/api/app/guardrails/save-user-guardrail`,
        body: input,
      },
      { apiName: this.apiName },
    );

  getMcpServerList = (input: GetMcpServersInput) =>
    this.restService.request<GetMcpServersInput, PagedResultDto<McpServerSummaryDto>>(
      {
        method: 'GET',
        url: '/api/app/mcp-servers',
        params: { ...input },
      },
      { apiName: this.apiName },
    );

  getMcpServer = (id: string) =>
    this.restService.request<void, McpServerDto>(
      {
        method: 'GET',
        url: `/api/app/mcp-servers/${id}`,
      },
      { apiName: this.apiName },
    );

  deleteMcpServer = (id: string) =>
    this.restService.request<void, void>(
      {
        method: 'DELETE',
        url: `/api/app/mcp-servers/${id}`,
      },
      { apiName: this.apiName },
    );

  createMcpServer = (input: McpServerDto) =>
    this.restService.request<McpServerDto, McpServerDto>(
      {
        method: 'POST',
        url: '/api/app/mcp-servers',
        body: input,
      },
      { apiName: this.apiName },
    );

  constructor(private restService: RestService) {}
}
